/**
 * Helpers para gravação do vídeo de confirmação (3s) via webcam.
 */

import {
  CONFIRMATION_VIDEO_TARGET_SECONDS,
  markConfirmationVideoRecordedDuration,
  normalizeConfirmationVideoMime,
} from './confirmationVideo';

export const CONFIRMATION_VIDEO_RECORD_MS = CONFIRMATION_VIDEO_TARGET_SECONDS * 1000;

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4',
] as const;

export function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

export function isCameraRecordingSupported(): boolean {
  if (typeof window === 'undefined') return false;
  const hasGetUserMedia = !!navigator.mediaDevices?.getUserMedia;
  const hasRecorder = typeof MediaRecorder !== 'undefined';
  return hasGetUserMedia && hasRecorder && !!pickRecorderMimeType();
}

export function extensionForVideoMime(mimeType: string): 'webm' | 'mp4' {
  return /mp4/i.test(mimeType) ? 'mp4' : 'webm';
}

export function blobToConfirmationVideoFile(
  blob: Blob,
  recordedAt: number = Date.now(),
  recordedDurationSeconds?: number,
): File {
  const mime = normalizeConfirmationVideoMime(blob.type) || 'video/webm';
  const ext = extensionForVideoMime(mime);
  const file = new File([blob], `confirmation-video-${recordedAt}.${ext}`, {
    type: mime,
    lastModified: recordedAt,
  });
  if (recordedDurationSeconds != null) {
    markConfirmationVideoRecordedDuration(file, recordedDurationSeconds);
  }
  return file;
}

export function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // ignore
    }
  }
}

export type CameraPermissionErrorKind =
  | 'denied'
  | 'not-found'
  | 'in-use'
  | 'unsupported'
  | 'unknown';

export function classifyCameraError(error: unknown): {
  kind: CameraPermissionErrorKind;
  message: string;
} {
  const name =
    error && typeof error === 'object' && 'name' in error
      ? String((error as { name?: string }).name)
      : '';

  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return {
      kind: 'denied',
      message:
        'Permissão de câmera negada. Autorize o acesso nas configurações do navegador ou use o upload manual.',
    };
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return {
      kind: 'not-found',
      message:
        'Nenhuma câmera foi encontrada neste dispositivo. Use o upload manual de um vídeo de 3 segundos.',
    };
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return {
      kind: 'in-use',
      message:
        'A câmera está em uso por outro aplicativo. Feche-o e tente novamente, ou use o upload manual.',
    };
  }
  if (name === 'NotSupportedError' || name === 'TypeError') {
    return {
      kind: 'unsupported',
      message:
        'Seu navegador não suporta gravação pela câmera. Use o upload manual (MP4/WEBM de 3s).',
    };
  }
  return {
    kind: 'unknown',
    message:
      error instanceof Error
        ? error.message
        : 'Não foi possível acessar a câmera. Tente novamente ou use o upload manual.',
  };
}
